import api from './api';

/**
 * Firm management service for Super Admins
 */
const firmService = {
  /**
   * Check if current user is a Super Admin
   * @returns {Promise<{is_super_admin: boolean}>}
   */
  checkSuperAdmin: async () => {
    const response = await api.get('/firms/check/super-admin');
    return response.data;
  },

  /**
   * Get firms the current user belongs to
   * @returns {Promise<Array>}
   */
  getUserFirms: async () => {
    const response = await api.get('/auth/firms');
    return response.data;
  },

  /**
   * Get all law firms (Super Admin only)
   * @returns {Promise<Array>}
   */
  getAllFirms: async () => {
    const response = await api.get('/firms/');
    return response.data;
  },

  /**
   * Get a specific firm by ID
   * @param {string} firmId - UUID of the firm
   * @returns {Promise<Object>}
   */
  getFirm: async (firmId) => {
    const response = await api.get(`/firms/${firmId}`);
    return response.data;
  },

  /**
   * Update a firm (Super Admin only for twilio_phone_number and is_active)
   * @param {string} firmId - UUID of the firm
   * @param {Object} firmData - Updated firm data
   * @returns {Promise<Object>}
   */
  updateFirm: async (firmId, firmData) => {
    const response = await api.put(`/firms/${firmId}`, firmData);
    return response.data;
  },

  /**
   * Switch active firm (Super Admin only)
   * @param {string} firmId - UUID of the firm to switch to
   * @returns {Promise<Object>}
   */
  switchFirm: async (firmId) => {
    const response = await api.post(`/auth/switch-firm/${firmId}`);
    return response.data;
  },

  /**
   * Create a new firm with optional data import (Super Admin only)
   * @param {Object} firmData - Firm creation data with optional import configuration
   * @returns {Promise<Object>} - Created firm and import job details
   */
  createFirm: async (firmData) => {
    const response = await api.post('/firms/', firmData);
    return response.data;
  },

  /**
   * Get AI settings for a firm (Super Admin only)
   * @param {string} firmId - UUID of the firm
   * @returns {Promise<Object>}
   */
  getAISettings: async (firmId) => {
    const response = await api.get(`/firms/${firmId}/ai-settings`);
    return response.data;
  },

  /**
   * Upload a logo image for a firm
   * @param {string} firmId - UUID of the firm
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} - { logo_url, logo_display_url }
   */
  uploadLogo: async (firmId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/firms/${firmId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Remove the firm's logo
   * @param {string} firmId - UUID of the firm
   * @returns {Promise<Object>}
   */
  deleteLogo: async (firmId) => {
    const response = await api.delete(`/firms/${firmId}/logo`);
    return response.data;
  },

  /**
   * Update AI settings for a firm (Super Admin only)
   * @param {string} firmId - UUID of the firm
   * @param {Object} data - { ai_model, monthly_token_limit }
   * @returns {Promise<Object>}
   */
  updateAISettings: async (firmId, data) => {
    const response = await api.put(`/firms/${firmId}/ai-settings`, data);
    return response.data;
  }
};

export default firmService;
